// Alert Configuration List Component
// Feature: 015-we-want-to
// T060: Component for displaying and managing alert configurations

import React from 'react';
import { Badge } from '../../ui/components/Badge';
import { useAlertConfigsByCollection } from '../../hooks/useAlertConfigs';
import {
  type AlertConfig,
  type NotificationType,
  getNotificationTypeLabel,
} from '../../api/alertConfigs';
import { FeatherBell, FeatherAlertCircle, FeatherInbox, FeatherMessageCircle, FeatherChevronRight } from '@subframe/core';
import { formatPrice } from '../../utils/formatters';

interface AlertConfigListProps {
  collectionId: string;
  onCreateAlert?: (notificationType: NotificationType) => void;
  /** Called when user clicks an alert card to edit it */
  onEditAlert?: (config: AlertConfig) => void;
}

export const AlertConfigList: React.FC<AlertConfigListProps> = ({
  collectionId,
  onCreateAlert,
  onEditAlert,
}) => {
  const { data, isLoading, error } = useAlertConfigsByCollection(collectionId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        <span className="ml-3 text-body font-body text-subtext-color">Loading alerts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 px-4 py-4 rounded-lg bg-error-50 border border-error-200">
        <FeatherAlertCircle className="h-5 w-5 text-error-600 flex-shrink-0" />
        <div className="flex flex-col gap-1">
          <span className="text-body-bold font-body-bold text-error-700">
            Failed to load alerts
          </span>
          <span className="text-caption font-caption text-error-600">
            Please try refreshing the page
          </span>
        </div>
      </div>
    );
  }

  const configs = data?.alert_configs || [];

  if (configs.length === 0) {
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
        <span className="text-body font-body text-subtext-color">
          No alerts configured. Create an alert above to get started.
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {configs.map((config) => (
        <AlertConfigCard
          key={config.id}
          config={config}
          onClick={() => onEditAlert?.(config)}
        />
      ))}
    </div>
  );
};

interface AlertConfigCardProps {
  config: AlertConfig;
  onClick?: () => void;
}

const AlertConfigCard: React.FC<AlertConfigCardProps> = ({
  config,
  onClick,
}) => {
  const hasFilters =
    config.min_amount_in_xrp !== null ||
    config.max_amount_in_xrp !== null ||
    (config.trait_filters && Object.keys(config.trait_filters).length > 0);

  const traitCount = config.trait_filters
    ? Object.values(config.trait_filters).reduce((total, values) => {
        // Handle edge case of double-stringified values from old records
        return total + (Array.isArray(values) ? values.length : 0);
      }, 0)
    : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start justify-between gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4 w-full text-left transition-colors hover:bg-neutral-50 hover:border-brand-300 cursor-pointer group"
    >
      {/* Left side - Alert details */}
      <div className="flex flex-1 flex-col gap-3">
        {/* Alert type and delivery channel */}
        <div className="flex items-center gap-2">
          <FeatherBell className="h-5 w-5 text-brand-600" />
          <span className="text-body-bold font-body-bold text-default-font">
            {getNotificationTypeLabel(config.notification_type)}
          </span>
          {/* Delivery channel indicator */}
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 text-subtext-color"
            title={config.delivery_channel === 'discord' ? 'Alerts sent to Discord' : 'Alerts sent to inbox'}
          >
            {config.delivery_channel === 'discord' ? (
              <FeatherMessageCircle className="h-3.5 w-3.5" />
            ) : (
              <FeatherInbox className="h-3.5 w-3.5" />
            )}
            <span className="text-caption font-caption">
              {config.delivery_channel === 'discord' ? 'Discord' : 'Inbox'}
            </span>
          </div>
        </div>

        {/* Filters */}
        {hasFilters ? (
          <div className="flex flex-col gap-2">
            {/* Price filters */}
            {(config.min_amount_in_xrp !== null || config.max_amount_in_xrp !== null) && (
              <div className="flex items-center gap-2">
                <span className="text-caption font-caption text-subtext-color">Price:</span>
                <Badge variant="neutral">
                  {config.min_amount_in_xrp !== null && config.max_amount_in_xrp !== null
                    ? `${formatPrice(config.min_amount_in_xrp)} - ${formatPrice(config.max_amount_in_xrp)}`
                    : config.min_amount_in_xrp !== null
                    ? `Min ${formatPrice(config.min_amount_in_xrp)}`
                    : `Max ${formatPrice(config.max_amount_in_xrp!)}`}
                </Badge>
              </div>
            )}

            {/* Trait filters */}
            {traitCount > 0 && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-caption font-caption text-subtext-color">Traits:</span>
                  <Badge variant="brand">
                    {traitCount} trait{traitCount !== 1 ? 's' : ''} selected
                  </Badge>
                </div>
                {config.trait_filters && Object.entries(config.trait_filters).map(([traitType, values]) => {
                  // Handle edge case of double-stringified values from old records
                  const valueArray = Array.isArray(values) ? values : [];
                  if (valueArray.length === 0) return null;

                  return (
                    <span key={traitType} className="text-caption font-caption text-subtext-color pl-4">
                      • {traitType}: {valueArray.join(', ')}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <span className="text-caption font-caption text-subtext-color">
            All events (no filters)
          </span>
        )}
      </div>

      {/* Right side - Chevron indicator */}
      <div className="flex items-center justify-center h-full self-center">
        <FeatherChevronRight className="h-5 w-5 text-subtext-color group-hover:text-brand-600 transition-colors" />
      </div>
    </button>
  );
};
